import { FormGroup } from "../core/FormGroup";
import { filterEventSelector } from "../util/event";
import {
  createFormError,
  createFormInfo,
  hideFormInfo,
  showFormInfo,
} from "../util/form";
import { createLogger } from "../util/logger";
import { delayPromise } from "../util/promise";
import {
  addRecaptchaTokenToFormData,
  isFormRecaptchaEnabled,
} from "../util/recaptcha";
import { Component } from "./Component";

const console = createLogger("FormComponent");

/**
 * Controls form submission and elements
 */
export class FormComponent extends Component {
  static name = "form";

  /**
   * Event triggered when form was submitted successful
   */
  static EVENT_SUCCESS = "form:success";

  get name() {
    return FormComponent.name;
  }

  /**
   * Constructor for the class.
   *
   * @param {HTMLFormElement} el
   * @param {Object} props - The props object.
   * @param {undefined|((res: Response) => Promise<any>)} props.onSubmit - Optional callback executed after form submission is complete
   * @param {bool} props.resetOnComplete - Default true
   *
   * @return {void}
   */
  constructor(el, props) {
    super(el);

    this.props = props || {};

    /**
     * @type {(HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement)[]}
     */
    this.inputs = Array.from(
      this.el.querySelectorAll("input, textarea, select")
    );

    /**
     * @type {Map<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement, FormGroup>}
     */
    this.formControls = new Map();
    this.inputs.forEach((input) => {
      const formGroup = input.closest(".form-group");
      if (!formGroup) return;

      this.formControls.set(input, new FormGroup(formGroup));
    });

    /**
     * @type {HTMLElement|null}
     */
    this.$formFooter = this.querySelector(".form__footer");

    /**
     * @type {HTMLElement|null}
     */
    this.$successOverlay = this.$("successOverlay");

    this.$successOverlayClose = this.$successOverlay?.querySelector(
      ".success-overlay__close"
    );

    this.addEventListener(this.inputs, "input", this.onInput.bind(this));

    this.addEventListener(this.el, "submit", this.onSubmit.bind(this));

    if (this.$successOverlay) {
      this.addEventListener(
        this.$successOverlay,
        "click",
        filterEventSelector("a", (e) => {
          this.hideSuccessOverlay();
        })
      );

      if (this.$successOverlayClose) {
        this.addEventListener(
          this.$successOverlayClose,
          "click",
          this.onClickCloseOverlay.bind(this)
        );
      }
    }
  }
    doCaptcha() {
        return new Promise((resolve) => {
            grecaptcha.enterprise.ready(() => {
                grecaptcha.enterprise.execute('6LeJuTsrAAAAAN9R9kCHU61cvgz9o1fy3biu78__', {action: "LOGIN"}).then((token) => {
                    return resolve(token);
                });
            });
        });
    }

    /**
     * Submit event listener
     * @param {SubmitEvent} e
     * @return Promise<object>
     */
    async onSubmit(e) {
        e.preventDefault();
        console.log("onSubmit", e);

    if (this._pending) return;
    this._pending = true;
    this.el.classList.add("form--pending");

    /**
     * @type {HTMLFormElement}
     */
    const form = e.target;
    const data = new FormData(form);

    this.resetFormErrors();

    // old captcha impl...
    //if (this.el.dataset.captcha) {
    //      const token = await this.doCaptcha();
    //      data.append("token", token || false);
    //};

    try {
      // Check if form has reCAPTCHA enabled
      if (isFormRecaptchaEnabled(form)) {
        console.log("Form has reCAPTCHA enabled, generating token...");

        // Generate reCAPTCHA token before submission
        const tokenAdded = await addRecaptchaTokenToFormData(
          data,
          data.get("action") || "DEFAULT"
        );
        if (!tokenAdded) {
          console.error("Failed to generate reCAPTCHA token");
          this.showFormError("reCAPTCHA validation failed. Please try again.");
          return;
        }
      }

      const res = await this.submitForm(form, data);
      return res;
    } catch (error) {
      console.error("Form submission error:", error);
      this.showFormError(
        "An error occurred while submitting the form. Please try again."
      );
    } finally {
      this._pending = false;
      this.el.classList.remove("form--pending");
    }
  }

  /**
   * Submit form data
   * @param {HTMLFormElement} form
   * @param {FormData} data
   * @return {Promise<object>}
   */
  async submitForm(form, data) {
    const response = await delayPromise(
      400,
      fetch(form.getAttribute("action"), {
        method: "POST",
        body: data,
      })
    );

    const res = await response.json();
    console.log("submitForm response", res, this.props);

    // Success
    if (!res.error) {
      if (this.$successOverlay) {
        this.showSuccessOverlay();
      } else {
        this.showFormInfo(res.message);

        setTimeout(() => {
          this.hideFormInfo();
        }, 4000);
      }

      if (this.props.resetOnComplete !== false) {
        this.el.reset();
      }

      this.dispatchDomEvent(FormComponent.EVENT_SUCCESS, {
        res,
      });
    } else {
      this.handleErrorResponse(res);
    }

    return this.props.onSubmit?.call(null, res) || res;
  }

  /**
   * Input event listener
   * @param {InputEvent} e
   */
  onInput(e) {
    const formGroup = e.target.closest(".form-group");

    if (!formGroup || !formGroup.classList.contains("form-group--invalid"))
      return;

    formGroup.querySelectorAll(".form-group__error").forEach((e) => e.remove());

    formGroup.classList.remove("form-group--invalid");

    // Remove previous error classes
    formGroup.classList.remove.apply(
      formGroup.classList,
      Array.from(formGroup.classList).filter((v) => v.startsWith("--error-"))
    );
  }

  /**
   * Click Handler when close overlay is clicked
   * @param {MouseEvent} e
   */
  onClickCloseOverlay(e) {
    e.preventDefault();
    e.stopPropagation();

    this.hideSuccessOverlay();
  }

  /**
   * Validates input elements value and returns an object of errors
   * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input
   * @return {{[error: string]: boolean}|false}
   */
  validateInput(input) {
    if (input.validity.valid) return false;

    const errors = {
      maxLength: input.validity.tooLong,
      minLength: input.validity.tooShort,
      required: input.validity.valueMissing,
      pattern: input.validity.patternMismatch,
    };

    return Object.keys(errors)
      .filter((key) => errors[key] === true)
      .reduce((result, key) => {
        return {
          ...result,
          [key]: errors[key],
        };
      }, {});
  }

  handleErrorResponse(res) {
    // Show form error message
    this.showFormError(res.error.message);

    if (!res.error.details) return;

    // Show errors on form contrls
    Object.keys(res.error.details).forEach((key) => {
      const input = this.getInputByName(key);

      if (!input) return;
      if (!this.formControls.has(input)) return;

      this.formControls.get(input).showError(res.error.details[key]);
    });
  }

  resetFormErrors() {
    this.hideFormError();
    this.hideFormInfo();

    this.formControls.forEach((group) => group.hideError());
  }

  showFormError(message) {
    if (!this.$formError) {
      this.$formError = createFormError(message);

      if (this.$formFooter) {
        this.$formFooter.parentElement.insertBefore(
          this.$formError,
          this.$formFooter
        );
      } else {
        this.el.append(this.$formError);
      }
    }

    showFormInfo(this.$formError, message);
  }

  hideFormError() {
    if (!this.$formError) return;

    hideFormInfo(this.$formError);
  }

  showFormInfo(message) {
    if (!this.$formInfo) {
      this.$formInfo = createFormInfo(message);
      if (this.$formFooter) {
        this.$formFooter.parentElement.insertBefore(
          this.$formInfo,
          this.$formFooter
        );
      } else {
        this.el.append(this.$formInfo);
      }
    }

    showFormInfo(this.$formInfo, message);
  }

  hideFormInfo() {
    if (!this.$formInfo) return;
    hideFormInfo(this.$formInfo);
  }

  /**
   * Finds input by name or undefined if not found
   * @param name
   * @return {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | undefined}
   */
  getInputByName(name) {
    return this.inputs.find((input) => input.name === name);
  }

  showSuccessOverlay() {
    this.$successOverlay?.classList?.add("form-success-overlay--visible");
  }

  hideSuccessOverlay() {
    this.$successOverlay?.classList?.remove("form-success-overlay--visible");
  }
  destroy() {
    console.log("Destroy");
    super.destroy();
    this.hideSuccessOverlay();
  }
}
